// client/src/components/admin/project/ProjectHistoryModal.js
import React, { useState, useEffect } from 'react';

const API_URL = process.env.REACT_APP_API_URL;

export default function ProjectHistoryModal({ open, onClose, projectId, projectNumber }) {
  const [histories, setHistories] = useState([]);
  const [loading, setLoading] = useState(false);
  
  // 추가/수정 모드
  const [mode, setMode] = useState(null); // null | 'create' | 'edit'
  const [editingHistory, setEditingHistory] = useState(null);
  const [content, setContent] = useState('');
  
  // 이력 조회
  const fetchHistories = async () => {
    if (!projectId) return;
    
    setLoading(true);
    try {
      const res = await fetch(
        `${API_URL}/api/admin/projects/${projectId}/history`,
        { credentials: 'include' }
      );
      const data = await res.json();
      
      if (data.ok) {
        setHistories(data.histories);
      } else {
        alert('이력 조회 실패');
      }
    } catch (error) {
      console.error('이력 조회 오류:', error);
      alert('이력 조회 중 오류가 발생했습니다.');
    } finally {
      setLoading(false);
    }
  };
  
  useEffect(() => {
    if (open && projectId) {
      fetchHistories();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, projectId]);
  
  // 이력 추가
  const handleCreate = async () => {
    if (!content.trim()) {
      alert('내용을 입력해주세요.');
      return;
    }
    
    try {
      const res = await fetch(`${API_URL}/api/admin/projects/${projectId}/history`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ content })
      });
      const data = await res.json();
      
      if (data.ok) {
        alert('이력이 추가되었습니다.');
        setMode(null);
        setContent('');
        fetchHistories();
      } else {
        alert(data.message || '이력 추가 실패');
      }
    } catch (error) {
      console.error('이력 추가 오류:', error);
      alert('이력 추가 중 오류가 발생했습니다.');
    }
  };
  
  // 이력 수정
  const handleUpdate = async () => {
    if (!content.trim()) {
      alert('내용을 입력해주세요.');
      return;
    }
    
    try {
      const res = await fetch(
        `${API_URL}/api/admin/projects/${projectId}/history/${editingHistory._id}`,
        {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json' },
          credentials: 'include',
          body: JSON.stringify({ content })
        }
      );
      const data = await res.json();
      
      if (data.ok) {
        alert('이력이 수정되었습니다.');
        setMode(null);
        setEditingHistory(null);
        setContent('');
        fetchHistories();
      } else {
        alert(data.message || '이력 수정 실패');
      }
    } catch (error) {
      console.error('이력 수정 오류:', error);
      alert('이력 수정 중 오류가 발생했습니다.');
    }
  };
  
  // 이력 삭제
  const handleDelete = async (historyId) => {
    if (!window.confirm('정말 삭제하시겠습니까?')) return;
    
    try {
      const res = await fetch(
        `${API_URL}/api/admin/projects/${projectId}/history/${historyId}`,
        {
          method: 'DELETE',
          credentials: 'include'
        }
      );
      const data = await res.json();
      
      if (data.ok) {
        alert('이력이 삭제되었습니다.');
        fetchHistories();
      } else {
        alert(data.message || '이력 삭제 실패');
      }
    } catch (error) {
      console.error('이력 삭제 오류:', error);
      alert('이력 삭제 중 오류가 발생했습니다.');
    }
  };
  
  if (!open) return null;
  
  return (
    <div className="project-history-modal" onClick={onClose}>
      <div className="project-history-modal__content" onClick={(e) => e.stopPropagation()}>
        <div className="project-history-modal__header">
          <div>
            <h2 className="project-history-modal__title">
              프로젝트 이력
            </h2>
            <p className="project-history-modal__subtitle">
              {projectNumber}
            </p>
          </div>
          <button onClick={onClose} className="project-history-modal__close">✕</button>
        </div>
        
        {mode === null ? (
          // 이력 목록 보기
          <div className="project-history-modal__body">
            <div style={{ marginBottom: 20 }}>
              <button
                onClick={() => setMode('create')}
                style={{
                  padding: '10px 20px',
                  background: '#3b82f6',
                  color: '#fff',
                  border: 'none',
                  borderRadius: 6,
                  cursor: 'pointer',
                  fontSize: 14,
                  fontWeight: 600,
                  boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)'
                }}
              >
                + 새 이력 추가
              </button>
            </div>
            
            {loading ? (
              <p className="project-history-modal__loading">로딩 중...</p>
            ) : histories.length === 0 ? (
              <p className="project-history-modal__empty">
                등록된 이력이 없습니다.
              </p>
            ) : (
              <div className="project-history-modal__timeline">
                {histories.map(history => (
                  <div key={history._id} className="project-history-modal__item">
                    <div className="project-history-modal__item-header">
                      <h3 className="project-history-modal__item-title">
                        {history.content.length > 50 ? history.content.substring(0, 50) + '...' : history.content}
                      </h3>
                      <span className="project-history-modal__item-date">
                        {new Date(history.createdAt).toLocaleString('ko-KR')}
                      </span>
                    </div>
                    <p className="project-history-modal__item-description">
                      {history.content}
                    </p>
                    <div className="project-history-modal__item-actor">
                      작성자: <span className="project-history-modal__item-actor-name">{history.adminName}</span>
                      {history.isEdited && (
                        <span className="project-history-modal__badge project-history-modal__badge--warning">
                          수정됨
                        </span>
                      )}
                    </div>
                    <div style={{ display: 'flex', gap: 8, marginTop: 12 }}>
                      <button
                        onClick={() => {
                          setMode('edit');
                          setEditingHistory(history);
                          setContent(history.content);
                        }}
                        style={{
                          padding: '6px 12px',
                          background: '#f3f4f6',
                          color: '#374151',
                          border: '1px solid #d1d5db',
                          borderRadius: 4,
                          cursor: 'pointer',
                          fontSize: 12,
                          fontWeight: 500
                        }}
                      >
                        수정
                      </button>
                      <button
                        onClick={() => handleDelete(history._id)}
                        style={{
                          padding: '6px 12px',
                          background: '#fee2e2',
                          color: '#dc2626',
                          border: '1px solid #fecaca',
                          borderRadius: 4,
                          cursor: 'pointer',
                          fontSize: 12,
                          fontWeight: 500
                        }}
                      >
                        삭제
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        ) : (
          // 이력 추가/수정 폼
          <div className="project-history-modal__body">
            <div style={{ marginBottom: 16 }}>
              <label style={{ display: 'block', marginBottom: 8, fontWeight: 600, fontSize: 14 }}>
                이력 내용 *
              </label>
              <textarea
                value={content}
                onChange={(e) => setContent(e.target.value)}
                maxLength={2000}
                rows={10}
                style={{
                  width: '100%',
                  padding: 12,
                  border: '1px solid #d1d5db',
                  borderRadius: 6,
                  fontSize: 14,
                  fontFamily: 'inherit',
                  resize: 'vertical',
                  lineHeight: 1.6,
                  boxSizing: 'border-box'
                }}
                placeholder="특이사항이나 처리 내용을 입력하세요..."
              />
              <div style={{ textAlign: 'right', fontSize: 12, color: '#6b7280', marginTop: 4 }}>
                {content.length} / 2000
              </div>
            </div>
            
            <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end' }}>
              <button
                onClick={() => {
                  setMode(null);
                  setEditingHistory(null);
                  setContent('');
                }}
                style={{
                  padding: '10px 20px',
                  background: '#f3f4f6',
                  color: '#374151',
                  border: '1px solid #d1d5db',
                  borderRadius: 6,
                  cursor: 'pointer',
                  fontSize: 14,
                  fontWeight: 600
                }}
              >
                취소
              </button>
              <button
                onClick={mode === 'create' ? handleCreate : handleUpdate}
                style={{
                  padding: '10px 20px',
                  background: '#3b82f6',
                  color: '#fff',
                  border: 'none',
                  borderRadius: 6,
                  cursor: 'pointer',
                  fontSize: 14,
                  fontWeight: 600,
                  boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)'
                }}
              >
                {mode === 'create' ? '추가' : '수정'}
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

